// TODO: keep track that focus stays on the same place during PLP update

import { scrollWindowTo } from 'widgets/toolbox/scroll';
import { submitFormJson } from 'widgets/toolbox/ajax';
import { TCart } from 'widgets/cart/CartMgr';
import { TProduct } from './ProductDetail';
import { TProcessButtonInstance } from 'widgets/global/ProcessButton';
import { TBundleItem, TBundleItemInstance } from 'widgets/product/BundleItem';
import { log } from 'widgets/toolbox/util';
import { TGTMInfo } from 'types/models/product';

type TProcessButtonPrefs = ReturnType<TProcessButtonInstance['prefs']>;

export interface IAddToCartButton extends TProcessButtonInstance {
    prefs(): TProcessButtonPrefs & {
        isOutOfStock?: boolean;
        stock: string;
    };
}

type TChildProduct = {
    pid: string;
    quantity: number;
}

type TAddProductRequestData = {
    pid: string;
    quantity: number;
    options?: string;
    childProducts?: string;
}

export type TAddToCartServerResponse = TServerResponse & {
    cart: TCart & {
        addedBonusProductsGtmInfo: Array<TGTMInfo>;
    };
}

/**
 * @description Base AddToCartMixin implementation
 * <br>This class is not intended to have a separate DOM representation.
 * @param ProductDetail Base widget for extending
 * @returns Add to Cart mixin widget
 */
function AddToCartMixinClassCreator(ProductDetail: import('widgets/product/ProductDetail').TProductDetail) {
    /**
     * @class AddToCartMixin
     * @augments ProductDetail
     * @property {string} data-text-network-error - network error text
     * @property {string} data-add-to-cart-label - add to cart label
     * @property {string} data-pid - product id
     * @property {boolean} data-show-minicart-on-product-add - Show minicart on product added to cart
     * @property {boolean} data-show-message-on-product-add - Show message on product added to cart
     * @property {boolean} data-show-alert-on-product-add - Show Global Alert on product added to cart
     */
    class AddToCartMixin extends ProductDetail {
        invalidBundleItems: Array<TBundleItemInstance> = [];

        bonusGtmInfo: Array<TGTMInfo> = [];

        prefs() {
            return {
                pid: '',
                processingRequest: false,
                textNetworkError: 'Network Error',
                addToCartBtn: 'addToCart',
                classesBusy: 'm-busy',
                showMinicartOnProductAdd: false,
                showMessageOnProductAdd: true,
                showAlertOnProductAdd: true,
                backInStockBtn: 'disclosureButton',
                backInStockForm: 'disclosureContent',
                backInStockSubscriptionEnable: false,
                productSetItem: false,
                ...super.prefs()
            };
        }

        /**
         * @description Widget initialization
         */
        init(): void {
            super.init();

            this.showBackInStockButton();

            this.product = this.product || {};

            this.getById<IAddToCartButton>(this.prefs().addToCartBtn, (addToCartBtn) => {
                const stock = addToCartBtn.prefs().stock;

                this.product.stock = parseInt(stock, 10);
            });
        }

        /**
         * @description Allows to toggle action buttons on PDP based on the received state
         * @param addToCartBtn Add to cart button
         * @param state Indicated toggle state
         */
        toggleActionButtons(addToCartBtn: TProcessButtonInstance, state = false): void {
            const backInStockBtn = this.ref(this.prefs().backInStockBtn);

            if (state) {
                addToCartBtn.hide();
                backInStockBtn.show();
            } else {
                addToCartBtn.show();
                backInStockBtn.hide();
            }
        }

        /**
         * @description Hides add to cart button if the product is out of stock and notify me when available functionality is enabled
         */
        showBackInStockButton(): void {
            if (this.prefs().backInStockSubscriptionEnable) {
                this.getById<TProcessButtonInstance>(this.prefs().addToCartBtn, (addToCartBtn) => {
                    addToCartBtn.hide();
                    this.ref(this.prefs().backInStockBtn).show();
                });
            }
        }

        /**
         * @description Handles response from server once product added to cart
         * @emits "productadded"
         * @emits "minicart.need.update"
         * @emits "product.added.to.cart"
         * @param response Add Product response
         */
        postAddProduct(response: TAddToCartServerResponse) {
            if (response.success && response.cart) {
                this.emit('productadded');
                const cartModel = response.cart;

                cartModel.showMinicart = this.prefs().showMinicartOnProductAdd;

                if (cartModel.addedBonusProductsGtmInfo) {
                    this.bonusGtmInfo = cartModel.addedBonusProductsGtmInfo;
                }

                if (response.addToCartOverlayUrl) {
                    this.eventBus().emit('dialogshow', {
                        url: response.addToCartOverlayUrl,
                        headerText: response.addToCartOverlayHeader,
                        ariaLabel: response.addToCartOverlayHeader,
                        bodyClass: 'm-has_dialog',
                        wrapperClasses: 'm-cart_overlay'
                    });
                }

                this.eventBus().emit('minicart.need.update', cartModel, this);
                this.eventBus().emit('product.added.to.cart', cartModel, this);
                this.eventBus().emit('bonus.product.added.to.cart', cartModel, this);

                if (this.prefs().showAlertOnProductAdd) {
                    const accessibilityAlert = this.prefs().accessibilityAlerts.addedtocart;

                    this.eventBus().emit('alert.show', {
                        accessibilityAlert
                    });
                }
            }

            if (!response.success || this.prefs().showMessageOnProductAdd) {
                this.eventBus().emit('product.addedTocart.with.error', response);
                this.showCartMessage(response.errorMessage, !response.success);
            }
        }

        /**
         * @description Verify product bundle items
         */
        prepareBundleItems(): Array<TChildProduct> {
            const { bundledProducts } = this.prefs();

            if (!bundledProducts || !bundledProducts.length) {
                return [];
            }

            const BundleItem = this.getConstructor('bundleItem') as TBundleItem;

            const nestedBundleItems = this.items.filter(
                (child) => child instanceof BundleItem
            ) as Array<TBundleItemInstance>;

            if (!nestedBundleItems.length) {
                if (!PRODUCTION) {
                    log.error('Cannot receive bundle items info for adding to the cart!');
                }

                this.setPref('readyToOrder', false);

                return [];
            }

            let availabilityError = false;

            const bundleItems: Array<TChildProduct> = [];

            nestedBundleItems.forEach(bundleItemWidget => {
                const bundleItemDetails = bundleItemWidget.getBundleInfo();

                if (bundleItemDetails) {
                    bundleItems.push({
                        pid: bundleItemDetails.pid,
                        quantity: bundleItemDetails.quantity
                    });
                }

                if (!bundleItemDetails.isAvailable || !bundleItemDetails.isAllAttributesSelected) {
                    availabilityError = true;
                }

                if (!bundleItemDetails.isAllAttributesSelected) {
                    this.invalidBundleItems.push(bundleItemWidget);
                }
            });

            this.setPref('readyToOrder', !availabilityError);

            return bundleItems;
        }

        showBundleErrors() {
            this.invalidBundleItems.forEach(item => {
                item.getById<TBundleItemInstance>(this.prefs().addToCartMsg, (bundleItemMsgWidget) => {
                    this.showCartMessage(this.prefs().textSelectOptions, true, bundleItemMsgWidget);
                });
            });

            this.invalidBundleItems[0].ref('nameContainer').focus();
            scrollWindowTo(this.invalidBundleItems[0].ref('self').get(), true);

            this.invalidBundleItems = [];
        }

        /**
         * @description Get selected quantity of product
         * This method is to be overwritten in ProductAvailabilityMixin for correct usage on PDP
         *
         * @returns the default quantity of the product that can be selected
         */
        getSelectedQuantity(): number {
            return this.prefs().selectedQuantity;
        }

        /**
         * @description Handles add to cart button click
         * @param button Add Product response
         * @param onBeforeSubmit Callback that executes before the request is submitted
         * @returns Promise object represents server response for session continuation
         */
        addToCart(button: TProcessButtonInstance, onBeforeSubmit?: () => void): Promise<unknown | null> {
            if (this.prefs().processingRequest) {
                return Promise.resolve(null);
            }

            const childProducts = this.prepareBundleItems();

            if (this.invalidBundleItems?.length) {
                this.showBundleErrors();

                return Promise.resolve(null);
            }

            if (!this.prefs().readyToOrder) {
                this.showCartMessage(this.prefs().textSelectOptions, true);

                return Promise.resolve(null);
            }

            if (this.product.stock && this.product.stock < this.prefs().selectedQuantity) {
                this.showCartMessage(this.prefs().textStockLimit, true);

                return Promise.resolve(null);
            }

            const selectedOptions = this.getSelectedProductOptions();
            const isSelectedOptionsValid = this.validateProductOptions(selectedOptions);

            if (!isSelectedOptionsValid) {
                return Promise.resolve(null);
            }

            this.setPref('processingRequest', true);

            button.startProcess();

            this.showProgressBar();

            const addToCartBtnPrefs = button.prefs();

            const requestData: TAddProductRequestData = {
                pid: this.prefs().currentProductId || addToCartBtnPrefs.pid,
                quantity: this.getSelectedQuantity() || addToCartBtnPrefs.selectedQuantity
            };

            if (selectedOptions.length) {
                requestData.options = JSON.stringify(selectedOptions);
            }

            if (childProducts.length) {
                requestData.childProducts = JSON.stringify(childProducts);
            }

            if (typeof onBeforeSubmit === 'function') {
                onBeforeSubmit();
            }

            return submitFormJson(addToCartBtnPrefs.addToCartUrl, requestData)
                .then((response) => {
                    this.setPref('processingRequest', false);
                    button.stopProcess();
                    this.hideProgressBar();

                    this.postAddProduct(response);

                    return response;
                })
                .catch(() => {
                    this.setPref('processingRequest', false);
                    button.stopProcess();
                    this.hideProgressBar();

                    this.showCartMessage(this.prefs().textNetworkError, true);
                });
        }

        showProgressBar(): void {
            this.ref('self').addClass(this.prefs().classesBusy);
        }

        hideProgressBar(): void {
            this.ref('self').removeClass(this.prefs().classesBusy);
        }

        /**
         * @description Update Product View
         * @param product productModel
         */
        updateProductView(product: TProduct) {
            super.updateProductView(product);
            this.updateBackInStockState(true, false);
            // Hide backInStock content on update product view
            this.callIfExists('hideContent');
            this.renderAddToCart(product);
        }

        /**
         * @description Hide cart message
         */
        hideCartMessage() {
            this.getById(this.prefs().addToCartMsg, (addToCartMsg) => {
                addToCartMsg.hide();
            });
        }

        /**
         * @description On quantity update handler
         * @param product productModel
         */
        onQuantityUpdate(product: TProduct) {
            this.hideCartMessage();
            this.renderAddToCart(product);
        }

        /**
         * @description Toggle Billing Form state based on passed state
         */
        renderBundleAddToCart() {
            if (this.prefs().bundledProducts && this.prefs().bundledProducts.length > 0) {
                const BundleItem = this.getConstructor('bundleItem');
                let isOutOfStock = false;

                this.eachChild<TBundleItemInstance>((child) => {
                    if (!(child instanceof BundleItem)) { return; }

                    if (!child.getBundleInfo().isAvailable) {
                        isOutOfStock = true;
                    }
                });

                this.product.availability.isOutOfStock = isOutOfStock;
                this.renderAddToCart(this.product);
            }
        }

        /**
         * @description Render Add to Cart button
         * @param product productModel
         */
        renderAddToCart(product: TProduct) {
            this.getById<IAddToCartButton>(this.prefs().addToCartBtn, (addToCartBtn) => {
                if (!product.available && product.availability.isOutOfStock) {
                    addToCartBtn.setPref('isOutOfStock', true);

                    addToCartBtn.setText(this.prefs().outOfStockLabel);

                    addToCartBtn.disable();

                    this.toggleActionButtons(addToCartBtn, product.backInStockSubscription?.isEnabled);
                } else if (product.availability.isReachedLimit) {
                    this.getById(this.prefs().addToCartMsg, (addToCartMsg) => {
                        const msg = product.availability.isReachedLimitMessage;
                        const error = true;

                        addToCartMsg.render(
                            'template',
                            { msg, error },
                            addToCartMsg.ref('container')
                        ).then(() => addToCartMsg.show());
                    });
                    addToCartBtn.setPref('isOutOfStock', true);

                    addToCartBtn.setText(this.prefs()[this.prefs().update ? 'updateLabel' : 'addToCartLabel']);

                    addToCartBtn.disable();
                    this.toggleActionButtons(addToCartBtn);
                } else {
                    addToCartBtn.setPref('isOutOfStock', false);

                    addToCartBtn.setText(this.prefs()[this.prefs().update ? 'updateLabel' : 'addToCartLabel']);

                    addToCartBtn.enable();
                    this.toggleActionButtons(addToCartBtn);
                }
            });
        }

        /**
         * @description Shows/Hides BackInStock components based on passed parameters
         * @param showForm shows/hides Back in Stock form
         * @param showSuccessBlock shows/hides success message after form submitting
         */
        updateBackInStockState(showForm: boolean, showSuccessBlock: boolean): void {
            this.ref('disclosureContent').toggleClass('m-success', showSuccessBlock);
            this.ref('contentBlock').toggle(showForm);
            this.ref('successBlock').toggle(showSuccessBlock);

            if (!showForm) {
                this.ref('disclosureButton').focus();
            }
        }

        /**
         * @description Handles backInStock form response.Show success message
         */
        handleBackInStockFormSubmit(): void {
            this.updateBackInStockState(false, true);
        }
    }

    return AddToCartMixin;
}

export type TAddToCartMixin = ReturnType<typeof AddToCartMixinClassCreator>;

export type TAddToCartMixinInstance = InstanceType<TAddToCartMixin>;

export default AddToCartMixinClassCreator;
